import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Target, 
  Users, 
  Shield, 
  TrendingUp, 
  Award,
  ArrowRight,
  User,
  Calendar,
  BarChart3,
  Mail
} from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';

const AboutPage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const coreValues = [
    {
      icon: Shield,
      title: getText('about.values.integrity'),
      description: getText('about.values.integrity.desc'),
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700'
    },
    {
      icon: Users,
      title: getText('about.values.collaboration'),
      description: getText('about.values.collaboration.desc'),
      color: 'emerald',
      gradient: 'from-emerald-500 to-emerald-600',
      bgGradient: 'from-emerald-50 to-emerald-100',
      borderColor: 'border-emerald-200',
      textColor: 'text-emerald-700'
    },
    {
      icon: TrendingUp,
      title: getText('about.values.innovation'),
      description: getText('about.values.innovation.desc'),
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-700'
    },
    {
      icon: Award,
      title: getText('about.values.excellence'),
      description: getText('about.values.excellence.desc'),
      color: 'amber',
      gradient: 'from-amber-500 to-amber-600',
      bgGradient: 'from-amber-50 to-amber-100',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-700'
    }
  ];

  const achievements = [
    {
      number: '500+',
      label: getText('about.achievements.members.label'),
      description: getText('about.achievements.members.desc'),
      icon: Users,
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      number: '50+',
      label: getText('about.achievements.events.label'),
      description: getText('about.achievements.events.desc'),
      icon: Calendar,
      gradient: 'from-emerald-500 to-emerald-600'
    },
    {
      number: '95%',
      label: getText('about.achievements.satisfaction.label'),
      description: getText('about.achievements.satisfaction.desc'),
      icon: Award,
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      number: '15+',
      label: getText('about.achievements.years.label'),
      description: getText('about.achievements.years.desc'),
      icon: BarChart3,
      gradient: 'from-amber-500 to-amber-600'
    }
  ];

  const quickLinks = [
    {
      title: getText('nav.about.submenu.president'),
      description: getText('about.president.subtitle') || 'Leadership that drives industrial excellence',
      href: '/about/president',
      icon: User,
      gradient: 'from-primary-600 to-primary-700',
      bgGradient: 'from-primary-50 to-primary-100',
      borderColor: 'border-primary-200'
    },
    {
      title: getText('nav.about.submenu.mission'),
      description: getText('about.mission.subtitle') || 'Our purpose and vision for the future',
      href: '/about/mission-vision',
      icon: Target,
      gradient: 'from-emerald-600 to-emerald-700',
      bgGradient: 'from-emerald-50 to-emerald-100',
      borderColor: 'border-emerald-200'
    },
    {
      title: getText('nav.about.submenu.structure'),
      description: getText('about.structure.subtitle') || 'How IFM is organized to serve industry better',
      href: '/about/structure',
      icon: Building2,
      gradient: 'from-blue-600 to-blue-700',
      bgGradient: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200'
    },
    // {
    //   title: getText('nav.about.submenu.achievements'),
    //   description: getText('about.achievements.subtitle') || 'Our milestones and accomplishments',
    //   href: '/about/achievements',
    //   icon: Award,
    //   gradient: 'from-purple-600 to-purple-700',
    //   bgGradient: 'from-purple-50 to-purple-100',
    //   borderColor: 'border-purple-200'
    // }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('about.title')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* About IFM Section */}
      <section className="section py-16 bg-gray-50">
        <div className="container-medium">
          <div className="w-full">
            <div className="text-center mb-8">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4 text-center">
                {getText('about.intro.title') || 'About IFM'}
              </h2>
            </div>
            
            <div className="bg-white p-8 md:p-10 shadow-sm w-full rounded-lg">
              <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
                <div className="space-y-3 text-base md:text-lg text-gray-700 leading-relaxed w-full">
                  <p>
                    {getText('home.about.federation.desc1') || 'The Industrial Fedration is a professional and reliable organization that works to resolve the regulatory, operational, and infrastructure challenges faced by industries.'}
                  </p>
                  <p>
                    {getText('home.about.federation.desc2') || 'We act as a crucial coordinating link between industries and various government departments, ensuring that industrial operations run smoothly, legally, and sustainably.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links to Subpages */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {getText('about.quickLinks.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getText('about.quickLinks.subtitle')}
            </p>
            <div className="mt-4">
              <div className="w-16 h-1 bg-gradient-to-r from-primary-600 to-emerald-500 mx-auto rounded-full"></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickLinks.map((link, index) => {
              const IconComponent = link.icon;
              return (
                <Link
                  key={index}
                  to={link.href}
                  className="group bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 hover:border-primary-400"
                >
                  <div className={`bg-gradient-to-br ${link.gradient} rounded-2xl w-16 h-16 flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                    {link.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5 group-hover:text-gray-700 transition-colors duration-300">
                    {link.description}
                  </p>
                  <div className="flex items-center text-primary-600 font-semibold text-sm group-hover:text-primary-700 group-hover:gap-2 transition-all duration-300">
                    <span>{getText('about.quickLinks.learnMore')}</span>
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Values Preview */}
      <section className="section py-16 bg-gray-50">
        <div className="container-medium">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {getText('about.values.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
              {getText('about.values.desc')}
            </p>
            <Link 
              to="/about/core-values" 
              className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 hover:gap-3 transition-all"
            >
              <span>{getText('about.values.viewAll')}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => {
              const IconComponent = value.icon;
              const hoverColorClass = {
                blue: 'group-hover:text-blue-700',
                emerald: 'group-hover:text-emerald-700',
                purple: 'group-hover:text-purple-700',
                amber: 'group-hover:text-amber-700'
              }[value.color] || '';
              
              return (
                <div key={index} className="group">
                  <div className={`bg-gradient-to-br ${value.bgGradient} rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 h-full border-2 ${value.borderColor} hover:border-opacity-100`}>
                    <div className={`bg-gradient-to-br ${value.gradient} rounded-2xl w-18 h-18 flex items-center justify-center mx-auto mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`} style={{width: '4.5rem', height: '4.5rem'}}>
                      <IconComponent className="h-9 w-9 text-white" />
                    </div>
                    <h3 className={`text-lg font-bold text-gray-900 mb-3 ${hoverColorClass} transition-colors duration-300`}>
                      {value.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-sm group-hover:text-gray-800 transition-colors duration-300">
                      {value.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievements Stats */}
      <section className="section py-16 bg-gradient-to-br from-gray-600 via-gray-700 to-gray-800 text-white">
        <div className="container-medium">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {getText('about.achievements.title')}
            </h2>
            <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              {getText('about.achievements.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <div key={index} className="group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full flex flex-col">
                  {/* Gradient Header */}
                  <div className={`bg-gradient-to-br ${achievement.gradient} p-6 text-white`}>
                    <div className="flex items-center justify-center mb-4">
                      <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-4 group-hover:scale-110 transition-all duration-300">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="text-5xl md:text-6xl font-bold mb-2 leading-tight group-hover:scale-105 transition-transform duration-300">
                      {achievement.number}
                    </div>
                    <div className="text-lg font-semibold opacity-95">
                      {achievement.label}
                    </div>
                  </div>

                  {/* Content Body */}
                  <div className="bg-white p-6 flex-grow flex flex-col">
                    <p className="text-sm text-gray-700 leading-relaxed text-center flex-grow">
                      {achievement.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>


    </div>
  );
};

export default AboutPage;
